import React, { useContext, useEffect, useRef, useState } from 'react';
import authContext from '../../../../../AuthContext/authContext';
import { STAFF_VERIFICATION_EVENT, staffSessionRequest } from '../../../../Graph/Authentication/staffSessionClient';
import './StaffSessionGate.css';

// UX only. The backend, not this component or role cookies, grants staff access.
export default function StaffSessionGate({ children, protectedRoute = true, fallbackShell = content => content, onSignedOut,
  // Default to asking the server. Only literal false is a temporary rollout
  // hold; neither this flag nor a browser role grants staff authority.
  enabled = process.env.REACT_APP_STAFF_SESSION_VERIFICATION_REQUIRED !== 'false' }) {
  const { token, baseUrl, logout } = useContext(authContext);
  const [state, setState] = useState({ token: null, phase: 'checking' });
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [retry, setRetry] = useState(0);
  const [nextSend, setNextSend] = useState(0);
  const actionGeneration = useRef(0);
  const actionController = useRef(null);
  const activeGate = enabled || retry > 0;
  useEffect(() => {
    if (!nextSend) return undefined;
    const timer = setTimeout(() => setNextSend(0), Math.max(0, nextSend - Date.now()));
    return () => clearTimeout(timer);
  }, [nextSend]);
  useEffect(() => {
    const demand = () => setRetry(value => value + 1);
    window.addEventListener(STAFF_VERIFICATION_EVENT, demand);
    return () => window.removeEventListener(STAFF_VERIFICATION_EVENT, demand);
  }, []);
  useEffect(() => {
    if (!token || !activeGate || !protectedRoute) {
      setBusy(false);
      setState({ token, protectedRoute, phase: 'allowed' });
      return undefined;
    }
    let live = true;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    setState({ token, protectedRoute, phase: 'checking' }); setBusy(false); setCode(''); setError(''); setNextSend(0);
    staffSessionRequest(baseUrl, token, '', undefined, { signal: controller.signal, requiredOnLegacy: true }).then(result => {
      if (live) setState({ token, protectedRoute, phase: result.required && !result.verified ? 'challenge' : 'allowed' });
    }).catch(err => {
      if (live) {
        setState({ token, protectedRoute, phase: 'error' });
        setError(err.status === 401 ? 'This sign-in has expired or is no longer valid. Sign out and sign in again.' : err.message);
      }
    }).finally(() => clearTimeout(timer));
    return () => { live = false; controller.abort(); clearTimeout(timer); };
  }, [token, baseUrl, retry, activeGate, protectedRoute]);
  useEffect(() => () => { actionGeneration.current++; actionController.current?.abort(); }, [token]);

  function signOut() {
    // Server revocation is best effort; a failed/expired session must never trap
    // this browser. Clearing local authentication does not grant staff access.
    actionGeneration.current++;
    actionController.current?.abort();
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 5000);
    try {
      Promise.resolve(staffSessionRequest(baseUrl, token, '/logout', {}, { signal: controller.signal }))
        .catch(() => {}).finally(() => clearTimeout(timer));
    } catch { clearTimeout(timer); }
    finally { logout(); onSignedOut?.(); }
  }

  async function perform(action) {
    if (busy) return;
    const generation = ++actionGeneration.current;
    setBusy(true); setError('');
    const controller = new AbortController();
    actionController.current = controller;
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const result = await staffSessionRequest(baseUrl, token, action,
        action === '/verify' ? { code } : action === '/challenge' ? { method: 'email' } : {}, { signal: controller.signal });
      if (generation !== actionGeneration.current) return;
      if (action === '/verify' && result.verified === true) {
        setCode(''); setRetry(value => value + 1); // Re-read server state, never trust a browser flag.
      } else if (action === '/challenge') {
        if (result.verified) setRetry(value => value + 1);
        else { setState({ token, protectedRoute, phase: 'code' }); setNextSend(Date.now() + 60000); }
      }
    } catch (err) { if (generation === actionGeneration.current) setError(err.message || 'Verification failed.'); }
    finally { clearTimeout(timer); if (generation === actionGeneration.current) setBusy(false); }
  }
  if (!token || !activeGate || !protectedRoute || (state.token === token && state.protectedRoute === protectedRoute && state.phase === 'allowed')) return children;
  const checking = state.token !== token || state.protectedRoute !== protectedRoute || state.phase === 'checking';
  return fallbackShell(<main className="staff-session-gate">
    <section className="staff-session-gate__card" aria-labelledby="staff-verification-title" aria-busy={checking || busy}>
      <div className="staff-session-gate__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 3 20 6v6c0 5-8 9-8 9s-8-4-8-9V6l8-3Z" />
          <path d="m8.5 12 2.5 2.5 4.5-5" />
        </svg>
      </div>
      <p className="staff-session-gate__eyebrow">Protected workspace</p>
      <h1 id="staff-verification-title">Verify your staff sign-in</h1>
      {checking ? <p className="staff-session-gate__status" role="status">Checking this session…</p> : <>
        <p className="staff-session-gate__description">Admin and moderator sessions require an email code. Verification applies only to this sign-in, not other browsers or accounts.</p>
        {state.phase === 'code' && <form className="staff-session-gate__form" onSubmit={event => { event.preventDefault(); perform('/verify'); }}>
          <label htmlFor="staff-session-code">Six-digit code sent to your account email</label>
          <input id="staff-session-code" type="text" inputMode="numeric" autoComplete="one-time-code"
            className="staff-session-gate__code" aria-describedby={`staff-session-help${error ? ' staff-session-error' : ''}`}
            value={code} maxLength={6} onChange={event => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))} />
          <button className="staff-session-gate__button staff-session-gate__button--primary" type="submit" disabled={busy || !/^\d{6}$/.test(code)}>Verify session</button>
        </form>}
        <div className="staff-session-gate__actions">
        {state.phase === 'error' ? <button className="staff-session-gate__button staff-session-gate__button--primary" type="button" disabled={busy} onClick={() => setRetry(value => value + 1)}>Retry session check</button> :
          <button className={`staff-session-gate__button ${state.phase === 'code' ? 'staff-session-gate__button--secondary' : 'staff-session-gate__button--primary'}`} type="button" disabled={busy || Date.now() < nextSend} onClick={() => perform('/challenge')}>
            {state.phase === 'code' ? 'Resend email code (wait 60 seconds)' : 'Send email code'}
          </button>}
        </div>
        {busy && <p className="staff-session-gate__status" role="status">Working securely…</p>}
        <p className="staff-session-gate__help" id="staff-session-help">Codes expire in 10 minutes. Never share a code. SMS is not enabled yet.</p>
      </>}
      {error && <p className="staff-session-gate__error" id="staff-session-error" role="alert">{error}</p>}
      <button className="staff-session-gate__button staff-session-gate__button--quiet staff-session-gate__signout" type="button" onClick={signOut}>Sign out</button>
    </section>
  </main>);
}
